import { useCallback, useEffect, useRef, useState } from "react";
import { afterWakeWord, canListen, canSpeak, pickVoice, recognitionCtor, speakable } from "@/lib/voice";

// Dictation. Push-to-talk fills the box so the person can fix what was heard;
// hands-free only reacts to a sentence that names her, and sends it straight.
export function useListening({ onText, handsFree = false, lang = "ro-RO" }) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState("");
  const recRef = useRef(null);
  const wantedRef = useRef(false);          // hands-free restarts itself; a stop must stick
  const onTextRef = useRef(onText);
  useEffect(() => { onTextRef.current = onText; }, [onText]);

  const stop = useCallback(() => {
    wantedRef.current = false;
    setListening(false);
    try { recRef.current?.stop(); } catch { /* already stopped */ }
  }, []);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    stop();
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = handsFree;
    rec.interimResults = false;
    rec.onresult = (event) => {
      const said = Array.from(event.results)
        .slice(event.resultIndex)
        .map((r) => r[0]?.transcript || "")
        .join(" ")
        .trim();
      if (!said) return;
      const text = handsFree ? afterWakeWord(said) : said;
      if (text) onTextRef.current?.(text);
    };
    rec.onerror = (event) => {
      // "no-speech" and "aborted" are the ordinary end of a phrase, not faults.
      if (!["no-speech", "aborted"].includes(event.error)) setError(event.error || "eroare");
      if (event.error === "not-allowed") wantedRef.current = false;
    };
    rec.onend = () => {
      // Recognition stops on its own after a pause. Hands-free means starting
      // again; push-to-talk means one phrase and done.
      if (wantedRef.current && handsFree) {
        try { rec.start(); return; } catch { /* fall through to off */ }
      }
      setListening(false);
    };
    recRef.current = rec;
    wantedRef.current = true;
    setError("");
    try {
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  }, [handsFree, lang, stop]);

  useEffect(() => stop, [stop]);            // never leave the microphone open

  return { supported: canListen(), listening, error, start, stop,
           toggle: () => (listening ? stop() : start()) };
}

// Speaking. Entirely local: speechSynthesis never sends audio anywhere.
export function useSpeaking(lang = "ro-RO") {
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    if (!canSpeak()) return undefined;
    // Held onto rather than read again on cleanup: the synthesiser the listener
    // was added to is the one it has to come off, whatever window looks like by
    // the time this unmounts.
    const synth = window.speechSynthesis;
    const load = () => setVoices(synth.getVoices() || []);
    load();
    synth.addEventListener?.("voiceschanged", load);
    return () => synth.removeEventListener?.("voiceschanged", load);
  }, []);

  const cancel = useCallback(() => {
    if (!canSpeak()) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, []);


  const speak = useCallback((markdown) => {
    if (!canSpeak()) return;
    const text = speakable(markdown);
    if (!text) return;
    window.speechSynthesis.cancel();        // one answer at a time
    const utter = new window.SpeechSynthesisUtterance(text);
    const voice = pickVoice(voices, lang);
    if (voice) { utter.voice = voice; utter.lang = voice.lang; }
    else utter.lang = lang;
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    setSpeaking(true);
    window.speechSynthesis.speak(utter);
  }, [voices, lang]);

  useEffect(() => cancel, [cancel]);        // leaving the panel stops the voice

  return { supported: canSpeak(), speaking, speak, cancel };
}
